import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { financeApiClient } from './apiClient';

/**
 * Helper to safely resolve effective organization ID from parameter or logged-in user session
 */
const resolveOrgId = (orgId) => {
  if (orgId !== undefined && orgId !== null && orgId !== '') {
    return Number(orgId);
  }
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const parsed = JSON.parse(userStr);
      if (parsed?.orgId || parsed?.org_id) {
        return Number(parsed.orgId || parsed.org_id);
      }
    }
  } catch (e) {
    // Ignore JSON parse errors
  }
  return 10;
};

/**
 * Service to manage finance and salary related operations according to Swagger documentation:
 * https://api.qa.techapurv.com/finance/swagger-ui/index.html
 * 
 * Base API structure: /finance/api
 */
export const financeService = {
  /**
   * Super list of all salary fields/components
   * GET /finance/api/v1/salary-fields/getAllSalaryFields
   */
  getAllSalaryFields: async () => {
    const response = await financeApiClient.get(API_ENDPOINTS.finance.salaryFields);
    return response.data;
  },

  /**
   * Get single salary field details by ID
   * GET /finance/api/v1/salary-fields/{id}
   */
  getSalaryFieldById: async (id) => {
    const response = await financeApiClient.get(API_ENDPOINTS.finance.salaryFieldById(id));
    return response.data;
  },

  /**
   * Organization assigned payroll components
   * GET /finance/api/v1/organizations/{orgId}/payroll-components
   */
  getCompanyPayrollComponents: async (orgId) => {
    const effectiveOrgId = resolveOrgId(orgId);
    const response = await financeApiClient.get(
      API_ENDPOINTS.finance.payrollComponents(effectiveOrgId)
    );
    return response.data;
  },

  /**
   * Save organization assigned payroll components
   * PUT /finance/api/v1/organizations/{orgId}/payroll-components
   * Payload: { salaryFieldIds: number[] }
   */
  saveCompanyPayrollComponents: async (orgId, salaryFieldIds) => {
    const effectiveOrgId = resolveOrgId(orgId);
    const payload = {
      salaryFieldIds: Array.isArray(salaryFieldIds) ? salaryFieldIds.map(Number) : [],
    };

    try {
      const response = await financeApiClient.put(
        API_ENDPOINTS.finance.payrollComponents(effectiveOrgId),
        payload
      );
      return response.data;
    } catch (err) {
      if (err.response?.status === 405) {
        const response = await financeApiClient.post(
          API_ENDPOINTS.finance.payrollComponents(effectiveOrgId),
          payload
        );
        return response.data;
      }
      throw err;
    }
  },

  /**
   * Fetch employee/user salary configuration
   * GET /finance/api/v1/users/{userId}/salary?orgId={orgId}&month={month}&year={year}
   * 
   * Compulsory query parameters:
   * - orgId: Organization ID (int32)
   * - month: Salary month formatted as 2 digits e.g. "09" for September (int32)
   * - year: Salary year (int32, e.g. 2026)
   * 
   * Example correct request:
   * https://api.qa.techapurv.com/finance/api/v1/users/5/salary?orgId=5&month=09&year=2026
   */
  getUserSalary: async (id, options = {}) => {
    // Support options as an object { orgId, month, year } or directly orgId
    const opts = typeof options === 'object' && options !== null ? options : { orgId: options };
    const { orgId, month, year } = opts;

    const now = new Date();
    const effectiveOrgId = resolveOrgId(orgId);
    
    // Format month as 2-digit string e.g. "09", defaulting to current month
    const rawMonth = month !== undefined && month !== null && month !== '' 
      ? Number(month) 
      : (now.getMonth() + 1);
    const formattedMonth = String(rawMonth).padStart(2, '0');

    // Default year to current year if not specified
    const effectiveYear = year !== undefined && year !== null && year !== '' 
      ? Number(year) 
      : now.getFullYear();

    const response = await financeApiClient.get(API_ENDPOINTS.finance.salary(id), {
      params: {
        orgId: effectiveOrgId,
        month: formattedMonth,
        year: effectiveYear,
      },
    });
    return response.data;
  },

  /**
   * Save employee/user salary configuration
   * PUT /finance/api/v1/users/{userId}/salary
   * 
   * Schema: UserSalarySaveRequest
   * Required fields: ["month", "orgId", "year"]
   */
  saveUserSalary: async (id, payload = {}) => {
    const now = new Date();
    const effectiveOrgId = resolveOrgId(payload.orgId);
    const rawMonth = payload.month !== undefined && payload.month !== null && payload.month !== ''
      ? Number(payload.month)
      : (now.getMonth() + 1);
    const effectiveYear = payload.year !== undefined && payload.year !== null && payload.year !== ''
      ? Number(payload.year)
      : now.getFullYear();

    const formattedPayload = {
      ...payload,
      orgId: effectiveOrgId,
      month: rawMonth,
      year: effectiveYear,
      totalCompensation: payload.totalCompensation !== undefined ? Number(payload.totalCompensation) : 0,
      variable: payload.variable !== undefined ? Number(payload.variable) : 0,
      components: Array.isArray(payload.components)
        ? payload.components.map((comp) => ({
            salaryFieldId: Number(comp.salaryFieldId || comp.id),
            value: Number(comp.value || 0),
          }))
        : [],
    };

    try {
      const response = await financeApiClient.put(
        API_ENDPOINTS.finance.salary(id),
        formattedPayload
      );
      return response.data;
    } catch (err) {
      if (err.response?.status === 405) {
        const response = await financeApiClient.post(
          API_ENDPOINTS.finance.salary(id),
          formattedPayload
        );
        return response.data;
      }
      throw err;
    }
  },

  /**
   * Disburse salary to employee
   * POST /finance/api/v1/salary/disburse
   */
  disburseSalary: async (payload) => {
    const response = await financeApiClient.post(
      API_ENDPOINTS.finance.disburse,
      payload
    );
    return response.data;
  },

  /**
   * Add user CTC record
   * POST /finance/api/v1/salary/ctc
   */
  addUserCtc: async (payload) => {
    const response = await financeApiClient.post(
      API_ENDPOINTS.finance.ctc,
      payload
    );
    return response.data;
  },

  /**
   * Preview salary slip
   * GET /finance/api/v1/salary-slips?userId={userId}&orgId={orgId}&month={month}&year={year}
   */
  getSalarySlip: async ({ userId, orgId, month, year } = {}) => {
    const now = new Date();
    const effectiveOrgId = resolveOrgId(orgId);
    const rawMonth = month !== undefined && month !== null && month !== '' 
      ? Number(month) 
      : (now.getMonth() + 1);
    const formattedMonth = String(rawMonth).padStart(2, '0');
    const effectiveYear = year !== undefined && year !== null && year !== '' 
      ? Number(year) 
      : now.getFullYear();

    const response = await financeApiClient.get(
      API_ENDPOINTS.finance.salarySlips,
      {
        params: {
          userId: Number(userId),
          orgId: effectiveOrgId,
          month: formattedMonth,
          year: effectiveYear,
        },
      }
    );
    return response.data;
  },

  /**
   * Download salary slip PDF
   * GET /finance/api/v1/salary-slips/download?userId={userId}&orgId={orgId}&month={month}&year={year}
   */
  downloadSalarySlip: async ({ userId, orgId, month, year } = {}) => {
    const now = new Date();
    const effectiveOrgId = resolveOrgId(orgId);
    const rawMonth = month !== undefined && month !== null && month !== '' 
      ? Number(month) 
      : (now.getMonth() + 1);
    const formattedMonth = String(rawMonth).padStart(2, '0');
    const effectiveYear = year !== undefined && year !== null && year !== '' 
      ? Number(year) 
      : now.getFullYear();

    const response = await financeApiClient.get(
      API_ENDPOINTS.finance.downloadSalarySlip,
      {
        params: {
          userId: Number(userId),
          orgId: effectiveOrgId,
          month: formattedMonth,
          year: effectiveYear,
        },
        responseType: 'blob',
      }
    );
    return response.data;
  },

  /**
   * Batch generate salary for organization
   * POST /finance/api/v1/{orgId}/salary/batch/gen?month={month}&year={year}
   * 
   * Path:
   * - orgId: Organization ID (int32)
   * Query:
   * - month: Salary month (int32)
   * - year: Salary year (int32)
   * 
   * Response:
   * 200 OK:
   * {
   *   orgId: number,
   *   month: number,
   *   year: number,
   *   employeeCount: number,
   *   generatedCount: number,
   *   csvS3Key: string,
   *   generatedPdfKeys: string[]
   * }
   */
  generateSalaryBatch: async (orgIdOrParams = {}, maybeMonth, maybeYear) => {
    let orgId, month, year;
    if (typeof orgIdOrParams === 'object' && orgIdOrParams !== null) {
      ({ orgId, month, year } = orgIdOrParams);
    } else {
      orgId = orgIdOrParams;
      month = maybeMonth;
      year = maybeYear;
    }

    const effectiveOrgId = resolveOrgId(orgId);
    const now = new Date();
    const effectiveMonth = month !== undefined && month !== null && month !== ''
      ? Number(month)
      : (now.getMonth() + 1);
    const effectiveYear = year !== undefined && year !== null && year !== ''
      ? Number(year)
      : now.getFullYear();

    const response = await financeApiClient.post(
      API_ENDPOINTS.finance.batchSalaryGen(effectiveOrgId),
      null,
      {
        params: {
          month: effectiveMonth,
          year: effectiveYear,
        },
      }
    );
    return response.data;
  },

  /**
   * Alias for generateSalaryBatch
   */
  batchGenerateSalary: async (...args) => {
    return financeService.generateSalaryBatch(...args);
  },

  /**
   * Fetch salary payments for an organization
   * GET /finance/api/v1/{orgId}/salary/payments?month={month}&year={year}
   *
   * Path:
   * - orgId: Organization ID (int32)
   * Query:
   * - month: Salary month (int32)
   * - year: Salary year (int32)
   *
   * Response 200 OK (text/csv): string[]
   */
  getSalaryPayments: async ({ orgId, month, year } = {}) => {
    const effectiveOrgId = resolveOrgId(orgId);
    const now = new Date();
    const effectiveMonth = month !== undefined && month !== null && month !== ''
      ? Number(month)
      : (now.getMonth() + 1);
    const effectiveYear = year !== undefined && year !== null && year !== ''
      ? Number(year)
      : now.getFullYear();

    const response = await financeApiClient.get(
      API_ENDPOINTS.finance.salaryPayments(effectiveOrgId),
      {
        params: {
          month: effectiveMonth,
          year: effectiveYear,
        },
      }
    );
    return response.data;
  },
};

export default financeService;
